import React, { useState } from 'react';
import {
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileOutlined,
  MoreOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Avatar,
  Typography,
  Space,
  Tag,
  Dropdown,
  Tooltip,
  Badge,
  Progress,
  message,
} from 'antd';
import dayjs from 'libs/dayjs';
import useFiles from '../../../hooks/useFiles';

const { Text, Title } = Typography;

const FileGrid = ({
  files = [],
  onEdit,
  onDelete,
  onPreview,
  onAddFile,
  user,
  folderName = 'Carpeta',
  folderColor = '#52c41a',
  emptyMessage = 'No hay archivos en esta carpeta',
  showAddButton = true,
  className = '',
}) => {
  const { isImage, getDownloadUrl, handlePreview, getAvatarProps } = useFiles();
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());

  // Función para manejar la descarga con indicador de inicio
  const handleDownload = async (file) => {
    const fileId = file.id;

    // Agregar archivo a la lista de descargas en progreso
    setDownloadingFiles((prev) => new Set([...prev, fileId]));

    try {
      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = getDownloadUrl(fileId);
      link.download = file.filename;

      // Iniciar descarga inmediatamente
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Mostrar mensaje de que la descarga comenzará
      message.info(`La descarga de ${file.filename} comenzará, por favor espere...`);

      // Quitar el indicador después de 2 segundos (tiempo suficiente para que el usuario vea el feedback)
      setTimeout(() => {
        setDownloadingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      message.error(`Error al iniciar descarga de ${file.filename}`);

      // Quitar el indicador en caso de error
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  // Función para obtener la extensión del archivo
  const getFileExtension = (filename) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toUpperCase() : '';
  };

  // Función para obtener el tamaño del archivo formateado
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Función para obtener el icono del archivo según su tipo
  const getFileTypeIcon = (fileType, filename) => {
    const extension = getFileExtension(filename).toLowerCase();

    if (fileType.startsWith('image/')) {
      return <FileImageOutlined className="text-blue-500" />;
    } else if (fileType === 'application/pdf' || extension === 'pdf') {
      return <FilePdfOutlined className="text-red-500" />;
    } else if (['doc', 'docx'].includes(extension)) {
      return <FileTextOutlined className="text-blue-600" />;
    } else if (['xls', 'xlsx'].includes(extension)) {
      return <FileTextOutlined className="text-green-600" />;
    } else {
      return <FileOutlined className="text-gray-500" />;
    }
  };

  // Función para obtener el color del tag según el tipo de archivo
  const getFileTypeColor = (fileType, filename) => {
    const extension = getFileExtension(filename).toLowerCase();

    if (fileType.startsWith('image/')) {
      return 'blue';
    } else if (fileType === 'application/pdf' || extension === 'pdf') {
      return 'red';
    } else if (['doc', 'docx'].includes(extension)) {
      return 'blue';
    } else if (['xls', 'xlsx'].includes(extension)) {
      return 'green';
    } else {
      return 'default';
    }
  };

  // Menú de acciones para cada archivo
  const getFileActions = (file) => [
    {
      key: 'preview',
      label: 'Vista previa',
      icon: <EyeOutlined />,
      onClick: () => {
        if (onPreview) {
          onPreview(file);
        } else {
          handlePreview(file);
        }
      },
      disabled: !isImage(file.file_type),
    },
    {
      key: 'download',
      label: downloadingFiles.has(file.id) ? 'Comenzando descarga...' : 'Descargar',
      icon: downloadingFiles.has(file.id) ? <LoadingOutlined /> : <DownloadOutlined />,
      onClick: () => handleDownload(file),
      disabled: downloadingFiles.has(file.id),
    },
    {
      key: 'edit',
      label: 'Editar',
      icon: <EditOutlined />,
      onClick: () => onEdit && onEdit(file),
      disabled: !user?.is_administrador,
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: 'Eliminar',
      icon: <DeleteOutlined />,
      onClick: () => onDelete && onDelete(file),
      disabled: !user?.is_administrador,
      danger: true,
    },
  ];

  // Estado vacío
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-8">
      <div className="text-center space-y-6">
        <div
          className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${folderColor}20, ${folderColor}40)`,
          }}
        >
          <FolderOutlined className="text-4xl" style={{ color: folderColor }} />
        </div>
        <div className="space-y-2">
          <Title level={4} className="text-gray-600 mb-0">
            {folderName} vacía
          </Title>
          <Text type="secondary" className="text-gray-500">
            {emptyMessage}
          </Text>
        </div>
        {showAddButton && onAddFile && (
          <Button
            type="primary"
            size="large"
            onClick={onAddFile}
            icon={<PlusOutlined />}
            className="rounded-lg px-8"
            style={{
              backgroundColor: folderColor,
              borderColor: folderColor,
            }}
          >
            Añadir primer archivo
          </Button>
        )}
      </div>
    </div>
  );

  if (!files || files.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}
    >
      {files.map((file, index) => (
        <Card
          key={file.id || index}
          className="group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border-0 bg-white"
          style={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
          styles={{ body: { padding: '16px' } }}
          cover={
            <div
              className="relative h-32 flex items-center justify-center overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${folderColor}20, ${folderColor}40)`,
              }}
            >
              {isImage(file.file_type) ? (
                <div className="w-full h-full flex items-center justify-center p-3">
                  <div className="relative w-full h-full flex items-center justify-center">
                    <img
                      src={getDownloadUrl(file.id)}
                      alt={file.title}
                      className="max-w-full max-h-full object-contain rounded-lg border-2 border-white shadow-lg"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                      }}
                      loading="lazy"
                      onError={(e) => {
                        // Fallback al Avatar si la imagen falla
                        e.target.style.display = 'none';
                        const fallback = e.target.nextSibling;
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                    <Avatar
                      {...getAvatarProps(file)}
                      size={80}
                      className="border-2 border-white shadow-lg"
                      style={{
                        display: 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-4xl mb-2">
                    {getFileTypeIcon(file.file_type, file.filename)}
                  </div>
                  <Tag
                    color={getFileTypeColor(file.file_type, file.filename)}
                    className="text-xs font-medium"
                  >
                    {getFileExtension(file.filename)}
                  </Tag>
                </div>
              )}

              {/* Overlay con acciones */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Space>
                  {isImage(file.file_type) && (
                    <Tooltip title="Vista previa">
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<EyeOutlined />}
                        onClick={() => {
                          if (onPreview) {
                            onPreview(file);
                          } else {
                            handlePreview(file);
                          }
                        }}
                        className="bg-white text-gray-700 hover:bg-gray-100"
                      />
                    </Tooltip>
                  )}
                  <Tooltip
                    title={downloadingFiles.has(file.id) ? 'Comenzando descarga...' : 'Descargar'}
                  >
                    <Button
                      type="primary"
                      shape="circle"
                      icon={
                        downloadingFiles.has(file.id) ? <LoadingOutlined /> : <DownloadOutlined />
                      }
                      onClick={() => handleDownload(file)}
                      disabled={downloadingFiles.has(file.id)}
                      className="bg-white text-gray-700 hover:bg-gray-100"
                    />
                  </Tooltip>
                  <Dropdown
                    menu={{ items: getFileActions(file) }}
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Tooltip title="Más opciones">
                      <Button
                        type="primary"
                        shape="circle"
                        icon={<MoreOutlined />}
                        className="bg-white text-gray-700 hover:bg-gray-100"
                      />
                    </Tooltip>
                  </Dropdown>
                </Space>
              </div>
            </div>
          }
        >
          <div className="space-y-3">
            {/* Título del archivo */}
            <div>
              <Text
                strong
                className="text-gray-800 text-sm leading-tight block"
                style={{
                  fontSize: '14px',
                  lineHeight: '1.4',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                title={file.title}
              >
                {file.title}
              </Text>
            </div>

            {/* Descripción */}
            {file.description && (
              <Text
                type="secondary"
                className="text-xs leading-relaxed block"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                title={file.description}
              >
                {file.description}
              </Text>
            )}

            {/* Información del archivo */}
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <Space size={4}>
                  <FileTextOutlined />
                  <span>{dayjs(file.createdAt).format('DD/MM/YYYY')}</span>
                </Space>
                {file.file_size && (
                  <Badge
                    count={formatFileSize(file.file_size)}
                    style={{
                      backgroundColor: '#f0f0f0',
                      color: '#666',
                      fontSize: '10px',
                      padding: '2px 6px',
                    }}
                  />
                )}
              </div>

              {/* Barra de progreso simulada para mostrar "completado" */}
              <Progress
                percent={100}
                size="small"
                showInfo={false}
                strokeColor={folderColor}
                className="opacity-50"
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default FileGrid;
